<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const sections = [
  {
    title: '🔢 Apa itu Sistem Nomor Antrian?',
    icon: 'ri-hashtag',
    color: 'primary',
    content: `
      <p class="mb-4">
        Setiap kali kasir selesai checkout untuk order <strong>Dine-in</strong> atau
        <strong>Takeaway</strong>, sistem otomatis memberi <strong>nomor antrian</strong> —
        ditampilkan besar di layar kasir dan (kalau memungkinkan) ikut tercetak di struk.
        Nomor ini yang dipakai untuk memanggil pelanggan saat pesanannya siap diambil.
      </p>
      <VAlert type="warning" variant="tonal">
        📢 <strong>Bukan panggilan suara otomatis.</strong> Sistem TIDAK akan berbunyi atau
        mengumumkan nomor sendiri — kasir tetap yang menyebut nomornya secara lisan
        ("Nomor 12, silakan diambil!"), sistem hanya membantu menampilkan &amp; melacak
        status setiap nomor.
      </VAlert>
      <p class="mt-4 mb-0">
        Nomor antrian mulai dari <strong>1 lagi setiap hari</strong> — jadi setiap pagi
        pelanggan pertama selalu dapat nomor 1, bukan lanjutan dari kemarin.
      </p>
    `,
  },
  {
    title: '⚙️ Cara Mengaktifkan — Pilih Tipe Order Mana yang Ikut Antrian',
    icon: 'ri-settings-3-line',
    color: 'success',
    content: `
      <p class="mb-4">
        Sistem antrian memakai halaman pengaturan yang sama dengan biaya per tipe order.
      </p>
      <ol class="mb-4">
        <li class="mb-2">
          Buka menu <strong>F&amp;B Modern → Tipe Order &amp; Delivery → Biaya per Tipe
          Order</strong>
        </li>
        <li class="mb-2">
          Di baris masing-masing tipe order (Dine-in / Takeaway / Delivery), cari sakelar
          <strong>"Ikut Sistem Antrian"</strong> — terpisah dari sakelar "Aktif" yang
          mengatur biaya layanan
        </li>
        <li class="mb-2">
          Nyalakan sakelar untuk tipe order yang mau dapat nomor antrian, matikan untuk yang
          tidak perlu
        </li>
        <li class="mb-0">Klik <strong>"Simpan Konfigurasi"</strong> di bagian bawah halaman</li>
      </ol>
      <VAlert type="info" variant="tonal">
        💡 <strong>Pengaturan bawaan (kalau belum pernah diubah):</strong> Dine-in &amp;
        Takeaway otomatis <strong>menyala</strong>, Delivery otomatis <strong>mati</strong> —
        karena pesanan Delivery diantar kurir ke pelanggan, bukan diambil sendiri di toko,
        jadi tidak relevan dipanggil nomornya. Anda tetap boleh menyalakannya manual kalau
        toko Anda punya kebutuhan khusus.
      </VAlert>
    `,
  },
  {
    title: '🏪 Toko Kecil Tanpa Kitchen Display (KDS)',
    icon: 'ri-store-2-line',
    color: 'info',
    content: `
      <p class="mb-4">
        Kalau toko Anda belum pakai layar dapur (Kitchen Display), sistem antrian tetap bisa
        dipakai <strong>sepenuhnya manual</strong> — tidak perlu aktifkan KDS sama sekali.
        Begini alurnya:
      </p>
      <ol class="mb-4">
        <li class="mb-2">Kasir checkout order Dine-in/Takeaway seperti biasa</li>
        <li class="mb-2">
          Nomor antrian langsung muncul besar di layar kasir begitu pembayaran selesai
        </li>
        <li class="mb-2">
          Kasir klik ikon <VChip size="x-small" variant="tonal" color="primary">#</VChip>
          <strong>"Papan Antrian"</strong> di bar atas layar POS (di samping ikon "Riwayat
          Order")
        </li>
        <li class="mb-2">
          Order yang baru checkout muncul di kolom <strong>"Menunggu"</strong> — begitu mulai
          dikerjakan, klik tombol <strong>"Tandai Diproses"</strong> pada kartunya (pindah ke
          kolom "Diproses")
        </li>
        <li class="mb-2">
          Begitu pesanan selesai dan siap diambil, klik <strong>"Tandai Siap Diambil"</strong>
          (pindah ke kolom "Siap Diambil")
        </li>
        <li class="mb-2">
          Kasir <strong>panggil nomornya secara lisan</strong> — sistem tidak bersuara sendiri
        </li>
        <li class="mb-0">
          Setelah pelanggan mengambil, klik <strong>"Tandai Sudah Diambil"</strong> — order
          hilang dari papan
        </li>
      </ol>
      <VAlert type="success" variant="tonal">
        ✅ Semua langkah di atas berjalan penuh tanpa Kitchen Display — cocok untuk warung/
        kedai kecil dengan 1 tablet kasir saja.
      </VAlert>
    `,
  },
  {
    title: '🍳 Restoran dengan Kitchen Display (KDS) Aktif',
    icon: 'ri-fire-line',
    color: 'warning',
    content: `
      <p class="mb-4">
        Kalau toko Anda sudah memakai <strong>Kitchen Display System</strong> (layar tiket di
        dapur/bar), status antrian bisa bergerak <strong>otomatis</strong> mengikuti tiket
        dapur — kasir tidak perlu klik apa pun.
      </p>
      <p class="mb-3">
        Yang perlu dipahami: 1 order bisa punya <strong>lebih dari 1 tiket dapur</strong>
        kalau isinya dari stasiun berbeda. Contoh: 1 order berisi kopi (dari stasiun Barista)
        + nasi goreng (dari stasiun Dapur) → ada 2 tiket terpisah.
      </p>
      <table class="userguide-table mb-4">
        <thead>
          <tr><th>Kondisi Tiket Dapur</th><th>Status Antrian</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Baru 1 dari 2 tiket yang selesai (mis. kopi sudah, nasi goreng belum)</td>
            <td><VChip size="small" color="info" variant="tonal">Diproses</VChip> — tetap menunggu tiket lainnya</td>
          </tr>
          <tr>
            <td><strong>SEMUA</strong> tiket dapur untuk order itu sudah selesai</td>
            <td><VChip size="small" color="success" variant="tonal">Siap Diambil</VChip> — otomatis berubah sendiri</td>
          </tr>
        </tbody>
      </table>
      <VAlert type="info" variant="tonal" class="mb-4">
        💡 Kasir tetap bisa buka panel <strong>Papan Antrian</strong> untuk memantau progres
        semua order — dan tetap <strong>BOLEH override manual</strong> kapan saja. Contoh:
        kasir lihat sendiri pesanan sudah diambil dari dapur padahal staf dapur belum sempat
        update layar KDS-nya — kasir bisa langsung klik "Tandai Sudah Diambil" tanpa menunggu.
      </VAlert>
      <p class="mb-0">
        Detail cara kerja Kitchen Display sendiri (stasiun, tiket, alur dapur) dijelaskan
        lengkap di panduan <strong>Kitchen Display</strong> — lihat halaman terkait di bawah.
      </p>
    `,
  },
  {
    title: '📺 Papan Antrian di Layar Kedua (Customer Display System)',
    icon: 'ri-tv-2-line',
    color: 'purple',
    content: `
      <p class="mb-4">
        Ini fitur <strong>tambahan opsional</strong> — menampilkan papan antrian di layar
        kedua yang menghadap pelanggan (mis. TV/tablet dipasang di dinding toko), supaya
        pelanggan bisa lihat sendiri progres nomornya tanpa perlu bertanya ke kasir.
      </p>
      <VAlert type="warning" variant="tonal" class="mb-4">
        💎 Butuh <strong>Customer Display System (CDS)</strong> aktif dulu — fitur eksklusif
        paket <strong>Premium</strong>. Kalau toko Anda belum punya akses CDS, pelajari
        dulu cara pairing dasarnya di panduan <strong>Customer Display System</strong>
        (lihat halaman terkait di bawah) sebelum lanjut ke bagian ini.
      </VAlert>
      <p class="mb-3">
        Saat pairing device kedua, sekarang ada <strong>2 pilihan mode</strong> — pilih di
        dialog "Hubungkan Layar Pelanggan" sebelum ambil kode 6 digitnya:
      </p>
      <table class="userguide-table mb-4">
        <thead>
          <tr><th>Mode</th><th>Menampilkan</th><th>Terputus Saat Shift Ditutup?</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Mode Transaksi</strong> (yang sudah ada)</td>
            <td>Keranjang belanja &amp; kembalian, per shift kasir</td>
            <td><VChip size="small" color="error" variant="tonal">Ya, terputus</VChip> — perlu pairing ulang tiap shift baru</td>
          </tr>
          <tr>
            <td><strong>Mode Papan Antrian</strong> (baru)</td>
            <td>2 kolom besar: "Sedang Diproses" &amp; "Siap Diambil"</td>
            <td><VChip size="small" color="success" variant="tonal">Tidak</VChip> — tetap tersambung, cocok dipasang permanen</td>
          </tr>
        </tbody>
      </table>
      <p class="mb-3">
        Karena <strong>Mode Papan Antrian tidak pernah terputus saat kasir ganti shift</strong>,
        device ini cocok dipasang <strong>permanen</strong> di dinding toko — tidak perlu
        pairing ulang setiap pagi atau setiap ganti kasir.
      </p>
      <p class="mb-3">
        Kalau toko Anda punya <strong>lebih dari 1 kasir aktif bersamaan</strong>, papan ini
        otomatis <strong>menggabungkan</strong> nomor antrian dari semua kasir jadi satu
        tampilan — pelanggan tidak perlu tahu order-nya diproses kasir mana.
      </p>
      <VAlert type="success" variant="tonal">
        ✅ Tampilan update sendiri secara real-time — begitu status sebuah nomor berubah
        (baik otomatis dari Kitchen Display maupun diklik manual kasir di Papan Antrian),
        layar pelanggan langsung ikut berubah tanpa perlu di-refresh.
      </VAlert>
    `,
  },
]

const faq = [
  {
    q: 'Kalau toko saya tidak pakai Kitchen Display sama sekali, apakah fitur nomor antrian tetap bisa dipakai?',
    a: 'Bisa, sepenuhnya. Fitur ini didesain agar bisa dipakai 100% manual lewat panel Papan Antrian di POS, tanpa perlu Kitchen Display aktif sama sekali — cocok untuk warung/kedai kecil dengan 1 kasir saja.',
  },
  {
    q: 'Apakah ada suara otomatis yang memanggil nomor pelanggan?',
    a: 'Tidak ada. Sistem murni menampilkan & melacak status nomor — kasir tetap yang menyebut nomornya secara lisan. Ini memang keputusan desain, bukan fitur yang belum selesai.',
  },
  {
    q: 'Kenapa order Delivery tidak dapat nomor antrian?',
    a: 'Karena secara default order Delivery diantar kurir langsung ke pelanggan, bukan diambil sendiri di toko — jadi tidak relevan dipanggil nomornya. Kalau toko Anda punya kebutuhan khusus, sakelar "Ikut Sistem Antrian" untuk Delivery tetap bisa dinyalakan manual di menu F&B Modern → Tipe Order & Delivery.',
  },
  {
    q: 'Apa beda kolom "Menunggu" dan "Diproses" di Papan Antrian?',
    a: '"Menunggu" berarti order baru saja checkout dan belum mulai dikerjakan. "Diproses" berarti pesanan sedang dibuat/dimasak. Kasir menggeser sendiri dari Menunggu ke Diproses (toko tanpa KDS), atau otomatis bergerak begitu Kitchen Display mencatat tiket mulai dikerjakan.',
  },
  {
    q: 'Bolehkah pasang 2 layar sekaligus — 1 Mode Transaksi dan 1 Mode Papan Antrian?',
    a: 'Boleh. Keduanya independen, dipasangkan lewat kode pairing terpisah ke 2 device berbeda. Umumnya Mode Transaksi dipasang menghadap kasir per shift, sementara Mode Papan Antrian dipasang permanen di area tunggu pelanggan.',
  },
  {
    q: 'Nomor antrian akan terus bertambah selamanya atau reset?',
    a: 'Reset otomatis setiap pergantian hari — nomor 1 selalu untuk pelanggan pertama di hari itu, tidak melanjutkan nomor dari hari sebelumnya.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Sistem Antrian</span>
        </div>
        <RouterLink to="/userguide">
          <VBtn variant="text" prepend-icon="ri-arrow-left-line" size="small" class="mb-4">
            Kembali ke Daftar Modul
          </VBtn>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="primary" variant="tonal" size="64" rounded="lg" class="me-4">
            <VIcon icon="ri-hashtag" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">
              Sistem Antrian
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Nomor antrian otomatis untuk Dine-in & Takeaway — dari manual sepenuhnya
              sampai terhubung Kitchen Display & layar pelanggan
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line" class="mb-0">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda mengaktifkan sistem nomor antrian, memakainya di toko yang belum
          punya Kitchen Display, memahami bedanya kalau Kitchen Display sudah aktif, dan
          (opsional) menampilkannya di layar kedua untuk pelanggan.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Sections -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VExpansionPanels variant="accordion" multiple>
          <VExpansionPanel v-for="(section, index) in sections" :key="index">
            <VExpansionPanelTitle>
              <VIcon :icon="section.icon" :color="section.color" class="me-3" />
              {{ section.title }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <div v-html="section.content" />
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Halaman Terkait -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'POS Kasir', sub: 'Panduan lengkap kasir dan transaksi', icon: 'ri-shopping-cart-2-line', color: 'success', to: { name: 'userguide-operasional-pos-kasir' } },
              { title: 'Tipe Order', sub: 'Dine-in, Takeaway, Delivery & biaya layanan', icon: 'ri-percent-line', color: 'success', to: { name: 'userguide-fb-modern-order-type' } },
              { title: 'Kitchen Display', sub: 'Stasiun dapur & tiket — dasar status antrian otomatis', icon: 'ri-fire-line', color: 'error', to: { name: 'userguide-fb-modern-kds' } },
              { title: 'Customer Display System', sub: 'Layar kedua menghadap pelanggan (Premium)', icon: 'ri-tv-2-line', color: 'primary', to: { name: 'userguide-kasir-lanjutan-cds' } },
            ]"
            :key="link.title"
            cols="12"
            md="6"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.userguide-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.userguide-table th,
.userguide-table td {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}

.userguide-table th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  font-weight: 600;
}
</style>
